import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function NumField({ label, value, onChange, testid, step = "1" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-600 text-sm">{label}</Label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-obsidian border-steel text-charcoal h-11" data-testid={testid} />
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isManagement = user?.role === "management_admin";
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!s) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="settings-loading" />;

  const dayW = s.day_weightings || { interview: 40, simulator: 60 };
  const wTotal = Number(dayW.interview || 0) + Number(dayW.simulator || 0);
  const setW = (k, v) => setS({ ...s, day_weightings: { ...dayW, [k]: Number(v) } });
  const setPay = (k, v) => setS({ ...s, pay_rates: { ...s.pay_rates, [k]: Number(v) } });
  const setCost = (k, v) => setS({ ...s, costs: { ...s.costs, [k]: Number(v) } });

  const gross = (s.costs.trainee_hourly * s.costs.contract_hours * s.costs.programme_weeks)
    + s.costs.instructor_allocation + s.costs.external_course + s.costs.ppe + s.costs.medical;
  const net = gross - s.costs.training_grant;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        weightings: s.weightings,
        day_weightings: { interview: Number(dayW.interview || 0), simulator: Number(dayW.simulator || 0) },
        assessment_question_count: Number(s.assessment_question_count),
        funding_areas: s.funding_areas,
        pay_rates: s.pay_rates,
        costs: s.costs,
        self_reschedule_enabled: !!s.self_reschedule_enabled,
        reschedule_cutoff_hours: Number(s.reschedule_cutoff_hours ?? 24),
        reminders_enabled: !!s.reminders_enabled,
      });
      toast.success("Settings saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="settings-heading">Academy Settings</h1>
        {!isManagement && <p className="text-sm text-amber-300 mt-1">Read-only — Management Admin access required to edit.</p>}
      </div>

      <fieldset disabled={!isManagement} className="space-y-6 disabled:opacity-70">
        <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4" data-testid="weightings-section">
          <div>
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Assessment Day Score</h2>
            <p className="text-xs text-slate-500 mt-1">Assessment Day = Interview + Simulator only. Live-machine practical is assessed inside the Training Academy.</p>
            <p className={`text-xs mt-1 font-mono ${wTotal === 100 ? "text-emerald-600" : "text-red-600"}`} data-testid="weightings-total">
              Total: {wTotal}% {wTotal !== 100 && "— must equal 100%"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <NumField label="Interview / attitude %" value={dayW.interview} onChange={(v) => setW("interview", v)} testid="w-interview" />
            <NumField label="Simulator aptitude %" value={dayW.simulator} onChange={(v) => setW("simulator", v)} testid="w-simulator" />
          </div>
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4" data-testid="assessment-config-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Online Assessment</h2>
          <div className="max-w-xs">
            <NumField label="Questions drawn per assessment" value={s.assessment_question_count} onChange={(v) => setS({ ...s, assessment_question_count: v })} testid="question-count-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Funding areas (comma separated)</Label>
            <Input value={s.funding_areas.join(", ")} onChange={(e) => setS({ ...s, funding_areas: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
              className="bg-obsidian border-steel text-charcoal h-11" data-testid="funding-areas-input" />
          </div>
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4" data-testid="booking-settings-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Assessment Day Booking</h2>
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <label className="flex items-center gap-2.5 text-sm text-charcoal cursor-pointer" data-testid="self-reschedule-toggle">
              <input type="checkbox" checked={!!s.self_reschedule_enabled} onChange={(e) => setS({ ...s, self_reschedule_enabled: e.target.checked })}
                className="h-4 w-4 accent-[#FFC800]" />
              Candidate self-reschedule ON/OFF
            </label>
            <NumField label="Self-reschedule cut-off (hours before)" value={s.reschedule_cutoff_hours ?? 24} onChange={(v) => setS({ ...s, reschedule_cutoff_hours: v })} testid="reschedule-cutoff-input" />
            <label className="flex items-center gap-2.5 text-sm text-charcoal cursor-pointer" data-testid="reminders-toggle">
              <input type="checkbox" checked={!!s.reminders_enabled} onChange={(e) => setS({ ...s, reminders_enabled: e.target.checked })}
                className="h-4 w-4 accent-[#FFC800]" />
              Automated reminders (7d / 48h / same-day)
            </label>
          </div>
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4" data-testid="pay-rates-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Progression Pay Framework (£/hr)</h2>
          <div className="grid grid-cols-3 gap-4">
            <NumField label="Trainee" value={s.pay_rates.trainee} onChange={(v) => setPay("trainee", v)} testid="pay-trainee" step="0.5" />
            <NumField label="Development Operator" value={s.pay_rates.development} onChange={(v) => setPay("development", v)} testid="pay-development" step="0.5" />
            <NumField label="Competent Operator" value={s.pay_rates.competent} onChange={(v) => setPay("competent", v)} testid="pay-competent" step="0.5" />
          </div>
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4" data-testid="costs-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Cost Per Trainee Calculator</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <NumField label="Trainee wage £/hr" value={s.costs.trainee_hourly} onChange={(v) => setCost("trainee_hourly", v)} testid="cost-wage" step="0.5" />
            <NumField label="Contract hrs/week" value={s.costs.contract_hours} onChange={(v) => setCost("contract_hours", v)} testid="cost-hours" />
            <NumField label="Programme weeks" value={s.costs.programme_weeks} onChange={(v) => setCost("programme_weeks", v)} testid="cost-weeks" />
            <NumField label="Instructor allocation £" value={s.costs.instructor_allocation} onChange={(v) => setCost("instructor_allocation", v)} testid="cost-instructor" step="0.5" />
            <NumField label="External course £" value={s.costs.external_course} onChange={(v) => setCost("external_course", v)} testid="cost-course" />
            <NumField label="Training grant £" value={s.costs.training_grant} onChange={(v) => setCost("training_grant", v)} testid="cost-grant" />
            <NumField label="PPE £" value={s.costs.ppe} onChange={(v) => setCost("ppe", v)} testid="cost-ppe" />
            <NumField label="Medical £" value={s.costs.medical} onChange={(v) => setCost("medical", v)} testid="cost-medical" />
          </div>
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="bg-obsidian rounded-sm p-4 text-center">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Gross cost</p>
              <p className="font-heading font-black text-2xl text-charcoal" data-testid="gross-cost">£{gross.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="bg-obsidian rounded-sm p-4 text-center">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Grants / support</p>
              <p className="font-heading font-black text-2xl text-emerald-300" data-testid="grants-total">−£{s.costs.training_grant.toLocaleString("en-GB")}</p>
            </div>
            <div className="bg-obsidian rounded-sm p-4 text-center border border-lomond/40">
              <p className="font-mono text-[10px] uppercase tracking-widest text-lomond">Net Lomond cost</p>
              <p className="font-heading font-black text-2xl text-lomond" data-testid="net-cost">£{net.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">Potential funding is never treated as received funding. Approved ERI is deducted only when confirmed.</p>
        </section>
      </fieldset>

      {isManagement && (
        <Button onClick={save} disabled={saving || wTotal !== 100} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12 px-10" data-testid="settings-save-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      )}
    </div>
  );
}
